
import { Spacer } from '@client/shared/design';
import {
  dayRangeLabel,
  multiDayRangeLabel,
  startOfDay,
  startOfWeek,
  weekRangeLabel,
} from '@client/shared/calendar/week-grid.ts';
import { cx } from '@client/shared/lib/cx.ts';
import { rangeDayCount, type SlotCalendarRange } from '@client/shared/calendar/slot-grid';
import styles from './CalendarToolbar.module.css';

export interface CalendarToolbarProps {
  anchor: Date;
  zone: string;
  range: SlotCalendarRange;
  onStep: (delta: -1 | 1) => void;
  onToday: () => void;
  canStepBack?: boolean;
  canStepForward?: boolean;
  onRangeChange?: (next: 'week' | 'day') => void;
  children?: React.ReactNode;
  label?: React.ReactNode;
  className?: string;
}

export function CalendarToolbar({
  anchor,
  zone,
  range,
  onStep,
  onToday,
  canStepBack = true,
  canStepForward = true,
  onRangeChange,
  children,
  label,
  className,
}: CalendarToolbarProps) {
  const rangeLabel =
    label ??
    (range === 'week'
      ? weekRangeLabel(startOfWeek(anchor, zone), zone)
      : range === '3day'
        ? multiDayRangeLabel(startOfDay(anchor, zone), zone, rangeDayCount(range))
        : dayRangeLabel(startOfDay(anchor, zone), zone));

  const periodName = range === 'week' ? 'week' : range === '3day' ? '3 days' : 'day';
  const prevName = `Previous ${periodName}`;
  const nextName = `Next ${periodName}`;

  return (
    <div
      className={cx(styles.toolbar, className)}
      role="toolbar"
      aria-label="Calendar navigation"
    >
      {children != null && <div className={styles.filters}>{children}</div>}

      {onRangeChange && (
        <div className={styles.viewToggle} role="group" aria-label="View range">
          <button
            type="button"
            className={cx(styles.segment, range === 'week' && styles.segmentActive)}
            aria-pressed={range === 'week'}
            onClick={() => onRangeChange('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={cx(styles.segment, range === 'day' && styles.segmentActive)}
            aria-pressed={range === 'day'}
            onClick={() => onRangeChange('day')}
          >
            Day
          </button>
        </div>
      )}

      <Spacer />

      <div className={styles.nav}>
        <button
          type="button"
          className={styles.navArrow}
          onClick={() => onStep(-1)}
          disabled={!canStepBack}
          aria-label={prevName}
        >
          <span aria-hidden="true">‹</span>
        </button>
        <span className={styles.navLabel} aria-live="polite">
          {rangeLabel}
        </span>
        <button
          type="button"
          className={styles.navArrow}
          onClick={() => onStep(1)}
          disabled={!canStepForward}
          aria-label={nextName}
        >
          <span aria-hidden="true">›</span>
        </button>
        <button type="button" className={styles.navToday} onClick={onToday}>
          Today
        </button>
      </div>
    </div>
  );
}
